import { useHomepagePageUserData } from '#src/app/pages/homepage/pageContext';
import type { HomepagePageUserData } from '#src/app/pages/homepage/pageContext';

type Variation = HomepagePageUserData['experiments']['homepageInputSources']['variation'];

/**
 * What the callout below the input shows when there's no error: the TikTok badge or the cycling
 * source badges.
 */
export type FormHelperVariant = 'tiktok' | 'source_logos';

/** The label above the input: "Start a petition to…", or "…from a post or an idea" with the cycling source badges. */
export type InputHeadingVariant = 'default' | 'post_or_idea';

type InputSourcesArm = Readonly<{
	/** Whether the input takes any allowlisted link rather than TikTok only; also picks the multi-source copy. */
	multiSource: boolean;
	/** Whether errors show inside the input, next to the submit button, rather than in the callout below it. */
	inlineMessages: boolean;
	/** The callout below the input. Unset on inline arms: the callout also carries errors, so it only shows when they don't render inline. */
	helper: FormHelperVariant | undefined;
	heading: InputHeadingVariant;
}>;

type Result = ModelHookResult<
	InputSourcesArm & {
		/** The arm's name, for tagging analytics events. */
		variation: Variation;
	}
>;

const SINGLE_SOURCE: InputSourcesArm = {
	multiSource: false,
	inlineMessages: false,
	helper: 'tiktok',
	heading: 'default',
};

const ARMS: Readonly<Record<Variation, InputSourcesArm>> = {
	// 'control' never renders this hero (see PageBody), but reads as the TikTok-only input if it did
	control: SINGLE_SOURCE,
	single_source: SINGLE_SOURCE,
	multi_source_logo_helper: { multiSource: true, inlineMessages: false, helper: 'source_logos', heading: 'default' },
	multi_source_logo_heading: { multiSource: true, inlineMessages: true, helper: undefined, heading: 'post_or_idea' },
};

/**
 * How the social post hero's link input behaves for the `homepage_input_sources` arm being rendered.
 */
export function useInputSourcesArm(): Result {
	const {
		data: { experiments },
	} = useHomepagePageUserData();

	const { variation } = experiments.homepageInputSources;

	return { data: { ...ARMS[variation], variation } };
}
